import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Cell from '../Cell';
import NotebookSEO from './NotebookSEO';
import LoginModal from './LoginModal';

const SharedNotebookView = () => {
    const { notebookId } = useParams();
    const navigate = useNavigate();
    const { user, token } = useAuth();

    const [notebook, setNotebook] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cloning, setCloning] = useState(false);
    const [isLoginOpen, setIsLoginOpen] = useState(false);

    // Get API base URL from config or env
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    useEffect(() => {
        fetchNotebook();
        fetchMetadata();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notebookId]);

    const fetchNotebook = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/public/notebooks/${notebookId}`);

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error("This notebook is private or does not exist.");
                }
                throw new Error("Failed to load notebook.");
            }

            const data = await response.json();
            setNotebook(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchMetadata = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/public/notebooks/${notebookId}/metadata`);
            if (response.ok) {
                const data = await response.json();
                setMetadata(data);
            }
        } catch (err) {
            console.error("Failed to fetch metadata", err);
        }
    };

    const handleClone = async () => {
        if (!user) {
            setIsLoginOpen(true);
            return;
        }

        try {
            setCloning(true);
            const response = await fetch(`${API_BASE_URL}/api/public/notebooks/${notebookId}/clone`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                await response.json();
                // Redirect to the new notebook
                navigate('/');
                // Ideally we should open the new notebook, but we don't have a direct route to open it in workspace yet
                // without selecting it. For now, go to workspace.
                // Or if workspace supports opening by ID in URL?
                // Assuming workspace loads last notebook or list.
            } else {
                alert("Failed to clone notebook.");
            }
        } catch (err) {
            console.error("Error cloning notebook:", err);
            alert("Error cloning notebook.");
        } finally {
            setCloning(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                    <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <Link to="/" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                        Go Home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {metadata && (
                <NotebookSEO
                    title={metadata.title}
                    description={metadata.description}
                    image={metadata.og_image}
                />
            )}

            {/* Header */}
            <header className="bg-white shadow-sm sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="flex items-center gap-2">
                            <img src="/logo/scifi_logo_transprent.png" alt="sciFi" className="h-8 w-auto hidden sm:block" />
                        </Link>
                        <div className="h-6 w-px bg-gray-300 mx-2"></div>
                        <h1 className="text-lg font-medium text-gray-900 truncate max-w-xs sm:max-w-md">
                            {notebook.title}
                        </h1>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Read Only
                        </span>
                    </div>

                    <div className="flex items-center gap-4">
                        {user ? (
                            <button
                                onClick={handleClone}
                                disabled={cloning}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {cloning ? 'Cloning...' : 'Clone to My Account'}
                            </button>
                        ) : (
                            <button
                                onClick={() => setIsLoginOpen(true)}
                                className="text-sm font-medium text-blue-600 hover:text-blue-500 bg-transparent border-none cursor-pointer"
                            >
                                Login to Run & Edit
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Notebook Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {notebook.cells && notebook.cells.length > 0 ? (
                            notebook.cells.map((cell) => (
                                <Cell
                                    key={cell.cell_id}
                                    cell={cell}
                                    readOnly={true}
                                    onUpdate={() => { }}
                                    onDelete={() => { }}
                                    onExecute={() => { }}
                                />
                            ))
                        ) : (
                            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                                <p className="text-gray-500">This notebook is empty.</p>
                            </div>
                        )}
                    </div>

                    {/* Sidebar / Chat History */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm sticky top-24">
                            <div className="p-4 border-b border-gray-200">
                                <h2 className="text-lg font-medium text-gray-900">Chat History</h2>
                            </div>
                            <div className="p-4 max-h-[calc(100vh-200px)] overflow-y-auto space-y-4">
                                {notebook.chat_history && notebook.chat_history.length > 0 ? (
                                    notebook.chat_history.map((msg, idx) => (
                                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                            <div
                                                className={`max-w-[85%] rounded-lg p-3 text-sm ${msg.role === 'user'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-900'
                                                    }`}
                                            >
                                                <p className="whitespace-pre-wrap">{msg.content}</p>
                                                <div className={`text-xs mt-1 ${msg.role === 'user' ? 'text-blue-200' : 'text-gray-500'}`}>
                                                    {new Date(msg.created_at).toLocaleTimeString()}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-center text-gray-500 text-sm py-4">No chat history.</p>
                                )}
                            </div>

                            {!user && (
                                <div className="p-4 border-t border-gray-200 bg-gray-50">
                                    <div className="text-center">
                                        <p className="text-sm text-gray-600 mb-3">Want to ask questions about this notebook?</p>
                                        <button
                                            onClick={() => setIsLoginOpen(true)}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                                        >
                                            Login to Chat
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
        </div>
    );
};

export default SharedNotebookView;
